import React, { useRef, useEffect, useState } from 'react';
import aiOptionIcon from '../assets/ai-option.png';
import aiModelIcon from '../assets/ai-model.png';
import saiDetailsIcon from '../assets/sai-details.png';
import approachIcon from '../assets/approach.png';
import algorithmIcon from '../assets/algorithm.png';
import mlConfigIcon from '../assets/ml-config.png';
import trainingIcon from '../assets/training.png';
import resultsIcon from '../assets/results.png';
import folderOpenIcon from '../assets/folder-open.png';
import slidersIcon from '../assets/sliders.png';

export type WizardStep =
  | 'ai-option'
  | 'ai-model'
  | 'sai-details'
  | 'approach'
  | 'existing-model'
  | 'existing-parameters'
  | 'existing-results'
  | 'visualize'
  | 'algorithm'
  | 'ml-config'
  | 'training'
  | 'results';

interface StepData {
  id: WizardStep;
  label: string;
  icon: string;
}

interface StepperProps {
  currentStep: WizardStep;
  isCustomModelFlow?: boolean;
  isExistingModelFlow?: boolean;
  onStepClick?: (step: WizardStep) => void;
  disableStepClick?: boolean;
}

export const Stepper: React.FC<StepperProps> = ({
  currentStep,
  isCustomModelFlow = false,
  isExistingModelFlow = false,
  onStepClick,
  disableStepClick = false,
}) => {
  const stepperRef = useRef<HTMLDivElement>(null);
  const [lineWidth, setLineWidth] = useState<number>(0);

  const baseSteps: StepData[] = [
    { id: 'ai-option', label: 'AI Option', icon: aiOptionIcon },
    { id: 'ai-model', label: 'AI Model', icon: aiModelIcon },
    { id: 'sai-details', label: 'SAI Details', icon: saiDetailsIcon },
    { id: 'approach', label: 'Approach', icon: approachIcon },
  ];

  const customModelSteps: StepData[] = [
    { id: 'algorithm', label: 'Algorithm', icon: algorithmIcon },
    { id: 'ml-config', label: 'ML Config', icon: mlConfigIcon },
    { id: 'training', label: 'Training', icon: trainingIcon },
    { id: 'results', label: 'Results', icon: resultsIcon },
  ];

  const existingModelSteps: StepData[] = [
    { id: 'existing-model', label: 'Model', icon: folderOpenIcon },
    { id: 'existing-parameters', label: 'Parameters', icon: slidersIcon },
    { id: 'existing-results', label: 'Results', icon: resultsIcon },
  ];

  let steps = baseSteps;
  if (isCustomModelFlow) {
    steps = [...baseSteps, ...customModelSteps];
  } else if (isExistingModelFlow) {
    steps = [...baseSteps, ...existingModelSteps];
  }
  const currentStepIndex = steps.findIndex(s => s.id === currentStep);

  useEffect(() => {
    const calculateLineWidth = () => {
      if (stepperRef.current && steps.length > 1) {
        const stepperWidth = stepperRef.current.offsetWidth;
        // Calculate the width of each line
        // The line should connect from one step to the next
        const calculatedWidth = stepperWidth / (steps.length - 1);
        setLineWidth(calculatedWidth);
      }
    };

    calculateLineWidth();
    window.addEventListener('resize', calculateLineWidth);

    return () => {
      window.removeEventListener('resize', calculateLineWidth);
    };
  }, [steps.length]);

  const handleStepClick = (step: StepData, index: number) => {
    // Only allow clicking on previous/completed steps, and not when step click is disabled (e.g. training in progress)
    if (!disableStepClick && index < currentStepIndex && onStepClick) {
      onStepClick(step.id);
    }
  };

  return (
    <div className="ai-stepper-container">
      <div className="ai-stepper" ref={stepperRef} style={{ '--line-width': `${lineWidth}px` } as React.CSSProperties}>
        {steps.map((step, index) => {
          const isActive = index === currentStepIndex;
          const isCompleted = index < currentStepIndex;
          const isClickable = !disableStepClick && isCompleted && onStepClick;

          return (
            <div
              key={step.id}
              className={`ai-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isClickable ? 'clickable' : ''}`}
              onClick={() => handleStepClick(step, index)}
              style={{
                cursor: isClickable ? 'pointer' : 'default'
              }}
            >
              <div className="ai-step-icon-container">
                <div className="ai-step-icon">
                  <img src={step.icon} alt={step.label} />
                </div>
              </div>
              <div className="ai-step-label">{step.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

